import { Component, OnInit } from '@angular/core';
import { ConfirmDialogComponent, Logger } from 'src/app/@shared';
import { FormControl, Validators, FormGroup } from '@angular/forms';
import { Store, VersionStoreService, VersionService, Version, VersionStoreDialogComponent } from '../..';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { combineLatest, distinctUntilChanged, map, Subscription } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SelectionModel } from '@angular/cdk/collections';

const log = new Logger('VersionStoreListComponent');

@Component({
  selector: 'app-version-store-list',
  templateUrl: './version-store-list.component.html',
  styleUrls: ['./version-store-list.component.scss']
})

export class VersionStoreListComponent<T extends Store, TVersion extends Version> implements OnInit {

  viewModel$ = combineLatest([
    this.versionStoreService.stores$,
    this.versionStoreService.isLoading$,
    this.versionStoreService.totalRecords$,
    this.versionStoreService.page$,
    this.versionService.getVersion(this.route.snapshot.parent?.params['versionId'])
  ]).pipe(
    map(([stores, isLoading, totalRecords, page, version]) => {
      if (stores.length == 0 && page.pageIndex > 0) {
        page.previousPageIndex = 0
        page.pageIndex = 0
        this.versionStoreService.page(page);
        this.versionStoreService.reload();
        }
      return { stores, isLoading, totalRecords, page, version }
    }),
  );
  storesList: T[] = [];
  flexMediaWatcher!: Subscription;
  displayedColumns = ['select', 'StoreName', 'ClientKey', 'Address1', 'Address2', 'City', 'State', 'PostalCode'];
  selection = new SelectionModel<T>(true, []);

  constructor(
    private versionStoreService: VersionStoreService<T>,
    private mediaObserver: MediaObserver,
    private versionService: VersionService<TVersion>,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
  ) { }

  ngOnInit(): void {
    log.debug('init');
    this.clearSearch();
    this.versionStoreService.reload();

    // detect changes in viewport size to handle show/hide of table columns
    const getAlias = (MediaChange: MediaChange[]) => {
      return MediaChange[0].mqAlias;
    };

    this.flexMediaWatcher = this.mediaObserver
      .asObservable()
      .pipe(
        distinctUntilChanged(
          (x: MediaChange[], y: MediaChange[]) => getAlias(x) === getAlias(y)
        ))
      .subscribe((change) => {
        if (change.some(x => x.mqAlias === 'xs')) {
          this.displayedColumns = ['select', 'StoreName', 'ClientKey'];
        }
        else if (change.some(x => x.mqAlias === 'sm')) {
          this.displayedColumns = ['select', 'StoreName', 'ClientKey', 'PostalCode'];
        }
        else {
          this.displayedColumns = ['select', 'StoreName', 'ClientKey', 'Address1', 'Address2', 'City', 'State', 'PostalCode'];
        }
      });

    // get and set the versionId in the version service
    if (this.route.snapshot.parent) {
      this.versionStoreService.versionId = this.route.snapshot.parent.params['versionId'];
    }

    this.versionStoreService.stores$.subscribe((data) => {
      this.storesList = data;
    });
  }

  onAssociateStore() {
    const dialogRef = this.dialog.open(VersionStoreDialogComponent, {
      width: '80%',
      height: '90%',
      data: { versionId: this.route.snapshot.parent?.params['versionId'] },
    });
    this.versionStoreService.dialogSearch('');
  }

  onSearch(event: any) {
    this.versionStoreService.search(event.target.value);
  }

  clearSearch() {
    this.filtersForm.controls.search.setValue('');
    this.versionStoreService.search('');
  }

  filtersForm = new FormGroup({
    search: new FormControl(),
  });

  onSort(sortState: Sort): void {
    this.versionStoreService.sort(sortState);
    this.selection.clear();
  }

  onPage(pageEvent: PageEvent): void {
    this.selection.clear();
    this.versionStoreService.page(pageEvent);
  }

  diassociateStore() {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message: `Do you want to remove from version?`,
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe(
      confirmResult => {
        if (confirmResult) {
          this.versionStoreService.disassociateVersionToStore(this.route.snapshot.parent?.params['versionId'], this.selection.selected).subscribe({
            next: () => {
              this.matSnackBar.open(`Stores deleted`, 'OK', DEFAULT_SNACKBAR_CONFIG);
              this.versionStoreService.reload();
              this.selection.clear();
            },
            error: (error) => {
              log.error('Error adding store to version', error);

              if (error.error.value) {
                throw new Error(error.error.value);
              } else {
                throw new Error(error.message);
              }
            }
          });
        }
      });

  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.storesList.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.storesList.forEach(row => this.selection.select(row));
  }

}
